export class RecipeInjector {
  _cardWrapper;
  _firstChange = true;
  listMutationObserver;

  /**
   * RecipeInjector is design to be a singleton
   * @param {HTMLElement} targetContainer target container
   * @param {CardInjectorWrapper} cardWrapper
   */
  constructor(container, cardWrapper, autoinject = false) {
    this._cardWrapper = cardWrapper;
    this.modifyHelperString();
    this.initListMutationObserver(container);

    // Case where the webcomponent is not inserted in the supplier's code
    // => We arbitrary decide where to inject the recipe cards
    if (autoinject) {
      this.injectCardsPlaceholders(container);
    }
  }

  /**
   * Init mutation Observer that will watch change on
   * targetContainer  and call onDomChangeCallback method
   * each time change is detected  and one time at init
   * @param {HTMLElement} targetContainer target container
   */
  initListMutationObserver(targetContainer) {
    const observedAtts = { attributes: false, childList: true, subtree: true };
    this.listMutationObserver = new MutationObserver(
      this.onDomChangeCallback.bind(this)
    );
    this.listMutationObserver.observe(targetContainer, observedAtts);
    this.onDomChangeCallback();
    this._firstChange = false;
    // console.debug('[Miam] dom lister init', this.listMutationObserver);
  }

  endMutationObserver() {
    this.listMutationObserver.disconnect();
  }

  /**
   * Action to do on dom changes
   */
  onDomChangeCallback() {
    const elementsDOM = this.getListElements();
    if (!this.needsUpdate(elementsDOM)) {
      return;
    }
    // console.debug('[Miam] dom change detected',elementsDOM);
    this.injectRecipes(elementsDOM);
  }

  /**
   * @param {DomElement} elementsDOM
   * @returns true if there is still a placeholder In DOM
   */
  needsUpdate(elementsDOM) {
    return elementsDOM?.length >= 1
  }

  /**
   * @returns placeholder DomElements list use to inject webcCard
   */
  getListElements() {
    return Array.from(document.getElementsByClassName("miamRecipeCard"));
  }

  /**
   * if there is a recipe in local storage
   * and if we can inject recipe in the current page open
   * a drawer with the stored recipe
   * and clear storage
   * @returns cachedRecipe or null
   */
  handleCachedRecipe() {
    //retro compatibility
    localStorage.removeItem("_miam/cached-recipe");

    const cachedRecipeUrl = sessionStorage.getItem('_miam/cachedRecipeUrl');

    // remove cached recipe if user has navigate
    // coursesu connexion page does not inject Miam so it is not considered a navigation
    if(cachedRecipeUrl != null && cachedRecipeUrl != window.location.href ){
      sessionStorage.removeItem('_miam/cachedRecipeUrl');
      sessionStorage.removeItem("_miam/cached-recipe");
      return null;
    }

    const cachedRecipe = JSON.parse(
      sessionStorage.getItem("_miam/cached-recipe")
    );

    // On DOM change without reloading
    if (!this._firstChange || !cachedRecipe || ! this._cardWrapper.shouldShowCachedRecipe() ) {
      return null;
    }

    // Show drawer if it's first change (page has reload) and there is a cachedRecipe
    // window.mealz?.recipes.display(cachedRecipe.id);
    sessionStorage.removeItem("_miam/cached-recipe");
    sessionStorage.removeItem('_miam/cachedRecipeUrl')
    return cachedRecipe;
  }
  /**
   * browse elementsDOM, get context and create webcCard for each element
   * and then inject it into DOM
   * @param {HTMLElement} elementsDOM to browse
   */
  injectRecipes(elementsDOM) {
    elementsDOM.forEach((_, index) => {
      this.injectRecipe(elementsDOM, index);
    });
  }

  /**
   * @param {Array<DomElement>} elementsDOM
   * @param {number} index
   * @returns return a form id card if threre is an id pass in placeholder dataset
   * otherwise return a display card if context is empty
   * otherwise return a standard webc card
   */
  buildCard(elementsDOM, index) {
    const context = this.getContext(elementsDOM, index);
    const forcedRecipeId = context.forcedRecipeId;
    const webcCard = document.createElement("webc-miam-recipe-card");
    if (forcedRecipeId) {
      webcCard.recipeId = forcedRecipeId;
    } else {
      context
        ? (webcCard.productIds = context.shelfIngredients ? context.shelfIngredients.map(i => ({ id: i })) : context)
        : (webcCard.randomModeEnable = true);
    }
    webcCard.headerText = this._cardWrapper.cardHeaderText();
    webcCard.displayVariant = this._cardWrapper.recipeCardVariant();
    webcCard.contextId = context.contextId;
    return webcCard;
  }

  /**
   * @param {Array<DomElement>} elementsDOM
   * @param {number} index
   */
  injectRecipe(elementsDOM, index) {
    const webcCard = this.buildCard(elementsDOM, index);
    if (webcCard) {
      this.appendToDOM(elementsDOM, index, webcCard);
      webcCard.addEventListener("hide", () => {
        // console.debug('[Miam] have hidden cards');
        webcCard.parentElement.style.display = "none";
      });
    }
  }

  /**
   * Update card helper message
   */
  modifyHelperString() {
    const helper = document.querySelector('webc-miam-recipe-helper');
    if (this._cardWrapper.recipeRecapLocationString() !== undefined) {
      helper.stringDetailLocation = this._cardWrapper.recipeRecapLocationString();
    }
  }

  /**
   * get data-context attribute on card placeholder
   * and return it as an object
   * @param {HTMLElement} elementsDOM list of placeholder div
   * @param {number} index of the current DOMelement
   * @returns context [{ id:ext_id_1 }, { id:ext_id_2 }, ...]
   */
  getContext(elementsDOM, index) {
    const context = JSON.parse(elementsDOM[index].dataset.context);
    context.contextId = elementsDOM[index].dataset.contextId;
    // If context is in previous format, map it to current format, else keep it as the new format
    return context;
  }

  /**
   * @param {Object} context [{ id:ext_id_1 }, { id:ext_id_2 }, ...]
   * @returns true is context attributes are empty of if context == null
   */
  isContextEmpty(context) {
    return (
      context?.shelfIngredients?.length < 1 &&
      context.currentIngredients?.length < 1 &&
      context.basketIngredients?.length < 1 &&
      context.length
    );
  }

  /**
   * inject our web component into DOM
   * @param {HTMLElement} elementsDOM list of placeholder div
   * @param {number} index  of the current DOMelement
   * @param {HTMLElement} webcCard element <webc-miam-recipe-card></webc-miam-recipe-card> to inject
   */
  appendToDOM(elementsDOM, index, webcCard) {
    elementsDOM[index].replaceWith(webcCard);
  }

  injectCardsPlaceholders(container) {
    const positions = this._cardWrapper.cardsPositionsByScreenWidth();
    const elements = Array.from(container.children);
    // console.debug('[Miam] Inject recipe cards placeholders @ positions:', positions);

    // Exclude injection of cards at the beginning of the array
    positions.filter(pos => pos > 0).forEach(position => {
      let cardContainer = document.createElement('div');
      cardContainer.classList.add("miamRecipeCardContainer");
      this._cardWrapper.cardContainerCssClasses().forEach(cssClass => {
        cardContainer.classList.add(cssClass);
      });

      let cardPlaceholder = document.createElement('div');
      cardPlaceholder.classList.add("miamRecipeCard");
      cardContainer.appendChild(cardPlaceholder);

      // Add context based on elements right before and after the injected placeholder
      const itemsIds = this._cardWrapper.getProductsIds(elements.slice(position - 1, position + 1));
      // console.debug('[Miam] Set context with items ids:', itemsIds);
      cardPlaceholder.dataset.context = JSON.stringify({ shelfIngredients: itemsIds });
      cardPlaceholder.dataset.contextId = position.toString();

      container.insertBefore(cardContainer, elements[position]);
    })
  }
}
